const UNREAD_TITLE_PATTERN = /^\(\d+\+?\)\s+/;
const FAVICON_SELECTOR = 'link[rel~="icon"]';
const originalFaviconHrefs = new WeakMap();
let faviconUpdateVersion = 0;

export const normalizeUnreadCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.trunc(parsed));
};

export const stripUnreadTitlePrefix = (title = '') =>
  String(title || '').replace(UNREAD_TITLE_PATTERN, '').trim() || 'Nettgrav';

export const formatUnreadDocumentTitle = (title, unreadCount) => {
  const baseTitle = stripUnreadTitlePrefix(title);
  const count = normalizeUnreadCount(unreadCount);
  if (!count) return baseTitle;
  return `(${count > 99 ? '99+' : count}) ${baseTitle}`;
};

const loadFaviconImage = (source) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });

export const syncUnreadFavicon = async (unreadCount) => {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return false;
  const links = Array.from(document.querySelectorAll(FAVICON_SELECTOR));
  if (links.length === 0) return false;

  links.forEach((link) => {
    if (!originalFaviconHrefs.has(link)) {
      originalFaviconHrefs.set(link, link.getAttribute('href') || '/favicon-192.png');
    }
  });

  const version = ++faviconUpdateVersion;
  const count = normalizeUnreadCount(unreadCount);
  if (count === 0) {
    links.forEach((link) => {
      link.setAttribute('href', originalFaviconHrefs.get(link) || '/favicon-192.png');
    });
    return true;
  }

  try {
    const source = originalFaviconHrefs.get(links[0]) || '/favicon-192.png';
    const image = await loadFaviconImage(source);
    if (version !== faviconUpdateVersion) return false;

    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const context = canvas.getContext('2d');
    if (!context) return false;

    context.clearRect(0, 0, 64, 64);
    context.drawImage(image, 2, 2, 54, 54);

    const styles = window.getComputedStyle?.(document.documentElement);
    const badgeColor = styles?.getPropertyValue('--ng-badge-alert').trim() || '#ef4444';
    const ringColor = styles?.getPropertyValue('--ng-badge-ring').trim() || '#ffffff';
    const label = count > 9 ? '9+' : String(count);

    context.beginPath();
    context.arc(48, 48, 15, 0, Math.PI * 2);
    context.fillStyle = badgeColor;
    context.fill();
    context.lineWidth = 4;
    context.strokeStyle = ringColor;
    context.stroke();
    context.fillStyle = ringColor;
    context.font = `700 ${label.length > 1 ? 17 : 22}px Arial, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(label, 48, 49);

    const dataUrl = canvas.toDataURL('image/png');
    if (version !== faviconUpdateVersion) return false;
    links.forEach((link) => link.setAttribute('href', dataUrl));
    return true;
  } catch {
    return false;
  }
};

export const syncInstalledAppBadge = async (unreadCount) => {
  if (typeof navigator === 'undefined') return false;
  const count = normalizeUnreadCount(unreadCount);
  try {
    if (count > 0 && typeof navigator.setAppBadge === 'function') {
      await navigator.setAppBadge(count);
      return true;
    }
    if (count === 0 && typeof navigator.clearAppBadge === 'function') {
      await navigator.clearAppBadge();
      return true;
    }
  } catch {}
  return false;
};
